import type { EstadoPropiedad, RequisitosActivacion } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { AccionConfirmada } from './AccionConfirmada.js';

interface Props {
  propiedadId: string;
  estado: EstadoPropiedad;
  requisitos: RequisitosActivacion | null;
  incidenciasAbiertas: number;
  contratoVigente: boolean;
  idioma: Idioma;
  onCambio: () => void;
  onError: (mensaje: string) => void;
}

/**
 * Ciclo de vida de la propiedad. Exclusivo de Direccion.
 *
 * Tres salidas y ninguna borra nada: suspender la retira del servicio sin tocar
 * el contrato, reactivar la devuelve si se cumple todo, y archivar la saca de la
 * cartera conservando su historia entera.
 *
 * Los controles se deshabilitan cuando el servidor los iba a rechazar, y se dice
 * POR QUE. Un boton que se puede pulsar y siempre falla es peor que uno apagado.
 */
export function PanelCicloVida({
  propiedadId,
  estado,
  requisitos,
  incidenciasAbiertas,
  contratoVigente,
  idioma,
  onCambio,
  onError,
}: Props) {
  const cambiar = (destino: EstadoPropiedad, motivo?: string) => {
    api
      .cambiarEstado(propiedadId, destino, motivo)
      .then(onCambio)
      .catch((fallo: unknown) =>
        onError(
          fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
        ),
      );
  };

  // Una propiedad archivada tiene una sola salida, y es la puerta del alta.
  // Nunca se crea una ficha nueva para corregir un archivado accidental: eso
  // partiria la historia de la villa en dos expedientes que nada relaciona.
  if (estado === 'ARCHIVED') {
    return (
      <section className="tarjeta">
        <h2>{t(idioma, 'cicloVida.titulo')}</h2>
        <p>{t(idioma, 'cicloVida.archivada')}</p>
        <div className="acciones">
          <AccionConfirmada
            etiqueta={t(idioma, 'cicloVida.restaurar')}
            consecuencias={t(idioma, 'cicloVida.restaurarConsecuencias')}
            idioma={idioma}
            pideMotivo
            onConfirmar={({ motivo }) => cambiar('ONBOARDING', motivo)}
          />
        </div>
      </section>
    );
  }

  const puedeReactivar = requisitos?.puedeActivarse === true;
  const puedeArchivar = incidenciasAbiertas === 0 && !contratoVigente;

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'cicloVida.titulo')}</h2>

      <div className="acciones">
        {estado === 'ACTIVE' && (
          <AccionConfirmada
            etiqueta={t(idioma, 'cicloVida.suspender')}
            consecuencias={t(idioma, 'cicloVida.suspenderConsecuencias')}
            idioma={idioma}
            pideMotivo
            onConfirmar={({ motivo }) => cambiar('SUSPENDED', motivo)}
          />
        )}

        {estado === 'SUSPENDED' && (
          <AccionConfirmada
            etiqueta={t(idioma, 'cicloVida.reactivar')}
            consecuencias={t(idioma, 'cicloVida.reactivarConsecuencias')}
            idioma={idioma}
            deshabilitado={!puedeReactivar}
            onConfirmar={() => cambiar('ACTIVE')}
          />
        )}

        <AccionConfirmada
          etiqueta={t(idioma, 'cicloVida.archivar')}
          consecuencias={t(idioma, 'cicloVida.archivarConsecuencias')}
          idioma={idioma}
          pideMotivo
          deshabilitado={!puedeArchivar}
          onConfirmar={({ motivo }) => cambiar('ARCHIVED', motivo)}
        />
      </div>

      {/* Lo que impide cada accion, dicho por su nombre. */}
      {estado === 'SUSPENDED' && !puedeReactivar && (
        <p>{t(idioma, 'propiedad.requisitosPendientes')}</p>
      )}
      {!puedeArchivar && (
        <ul>
          {contratoVigente && <li>{t(idioma, 'propiedad.contratoVigente')}</li>}
          {incidenciasAbiertas > 0 && <li>{t(idioma, 'propiedad.incidenciasAbiertas')}</li>}
        </ul>
      )}
    </section>
  );
}
